/**
 * Operator content routes (Option B): scope prefixes encode entry context;
 * asset id lives in the path for asset screens.
 */
import {
  ASSET_ROOT_CANONICAL_ER_SCREEN_IDS,
  MAKE_A_REQUEST_SCREEN_ID,
  screenIdToUrlSlug,
  urlSlugToScreenId,
} from '@buxus/shared'
export type OperatorUrlSlugResolver = (operatorId: string) => string

export const DEFAULT_OPERATOR_SCREEN = 'screen.operatorOverview'

export type RouteScope = 'operator' | 'assets' | 'in-an-emergency'

export type OperatorContentRoute = {
  scope: RouteScope
  screenId: string
  assetId: string | null
}

const SCOPE_ASSETS_SEGMENT = 'assets'
const SCOPE_EMERGENCY_SEGMENT = 'in-an-emergency'

export function isAssetContentScreen(screenId: string): boolean {
  return screenId.startsWith('screen.asset') && screenId !== 'screen.assets'
}

/** ER hub or a screen reached through the ER hub (not asset-root canonical duplicates). */
export function isErSubtreeScreen(screenId: string): boolean {
  if (screenId === 'screen.assetEmergencyResponse') return true
  if (!screenId.startsWith('screen.assetEmergency')) return false
  return !ASSET_ROOT_CANONICAL_ER_SCREEN_IDS.has(screenId)
}

function stackSegmentToScreenId(seg: string): string | null {
  const fromSlug = urlSlugToScreenId(seg)
  if (fromSlug) return fromSlug
  if (/^screen\./i.test(seg)) return seg
  return null
}

/** Menu ancestry stack derived from a parsed URL route (internal state / breadcrumbs). */
export function menuStackFromRoute(route: OperatorContentRoute): string[] {
  const { scope, screenId } = route
  const base = [DEFAULT_OPERATOR_SCREEN]

  if (scope === 'operator') {
    if (screenId === DEFAULT_OPERATOR_SCREEN) return [screenId]
    return [...base, screenId]
  }

  if (scope === 'assets') {
    if (screenId === 'screen.assets') return [...base, 'screen.assets']
    if (screenId === 'screen.assetOverview') {
      return [...base, 'screen.assets', 'screen.assetOverview']
    }
    if (isErSubtreeScreen(screenId)) {
      return [
        ...base,
        'screen.assets',
        'screen.assetOverview',
        'screen.assetEmergencyResponse',
        screenId,
      ]
    }
    return [...base, 'screen.assets', 'screen.assetOverview', screenId]
  }

  if (scope === 'in-an-emergency') {
    if (screenId === 'screen.inAnEmergency') return [...base, 'screen.inAnEmergency']
    return [
      ...base,
      'screen.inAnEmergency',
      'screen.assetEmergencyResponse',
      screenId,
    ]
  }

  return [screenId]
}

/** Build a route from the internal menu stack + optional asset id. */
export function routeFromMenuStack(
  stack: string[],
  assetId?: string | null,
): OperatorContentRoute {
  const screenId = stack[stack.length - 1] ?? DEFAULT_OPERATOR_SCREEN

  if (stack.includes('screen.inAnEmergency')) {
    if (screenId === 'screen.inAnEmergency') {
      return { scope: 'in-an-emergency', screenId, assetId: null }
    }
    return { scope: 'in-an-emergency', screenId, assetId: assetId ?? null }
  }

  if (screenId === 'screen.assets') {
    return { scope: 'assets', screenId, assetId: null }
  }

  if (isAssetContentScreen(screenId)) {
    return { scope: 'assets', screenId, assetId: assetId ?? null }
  }

  return { scope: 'operator', screenId, assetId: null }
}

export function parseOperatorContentRoute(restSegments: string[]): OperatorContentRoute | null {
  if (restSegments.length === 0) {
    return { scope: 'operator', screenId: DEFAULT_OPERATOR_SCREEN, assetId: null }
  }

  const first = restSegments[0]!

  if (first === SCOPE_ASSETS_SEGMENT) {
    if (restSegments.length === 1) {
      return { scope: 'assets', screenId: 'screen.assets', assetId: null }
    }
    const assetId = decodeURIComponent(restSegments[1]!)
    if (restSegments.length === 2) {
      return { scope: 'assets', screenId: 'screen.assetOverview', assetId }
    }
    const screenId = stackSegmentToScreenId(decodeURIComponent(restSegments[2]!))
    if (!screenId) return null
    return { scope: 'assets', screenId, assetId }
  }

  if (first === SCOPE_EMERGENCY_SEGMENT) {
    if (restSegments.length === 1) {
      return { scope: 'in-an-emergency', screenId: 'screen.inAnEmergency', assetId: null }
    }
    const assetId = decodeURIComponent(restSegments[1]!)
    if (restSegments.length === 2) {
      return {
        scope: 'in-an-emergency',
        screenId: 'screen.assetEmergencyResponse',
        assetId,
      }
    }
    const screenId = stackSegmentToScreenId(decodeURIComponent(restSegments[2]!))
    if (!screenId) return null
    return { scope: 'in-an-emergency', screenId, assetId }
  }

  const screenId = stackSegmentToScreenId(decodeURIComponent(first))
  if (!screenId) return null
  return { scope: 'operator', screenId, assetId: null }
}

export function pathForOperatorRoute(
  operatorId: string,
  route: OperatorContentRoute,
  urlSlugForOperator?: OperatorUrlSlugResolver,
): string {
  const slug = urlSlugForOperator?.(operatorId)?.trim() || operatorId
  const opSeg = encodeURIComponent(slug)
  const { scope, screenId, assetId } = route

  if (scope === 'operator') {
    if (screenId === DEFAULT_OPERATOR_SCREEN) return `/operators/${opSeg}`
    if (screenId === MAKE_A_REQUEST_SCREEN_ID) {
      return `/operators/${opSeg}/${encodeURIComponent(screenIdToUrlSlug(screenId))}`
    }
    return `/operators/${opSeg}/${encodeURIComponent(screenIdToUrlSlug(screenId))}`
  }

  if (scope === 'assets') {
    if (screenId === 'screen.assets') return `/operators/${opSeg}/${SCOPE_ASSETS_SEGMENT}`
    if (!assetId) return `/operators/${opSeg}/${SCOPE_ASSETS_SEGMENT}`
    if (screenId === 'screen.assetOverview') {
      return `/operators/${opSeg}/${SCOPE_ASSETS_SEGMENT}/${encodeURIComponent(assetId)}`
    }
    return `/operators/${opSeg}/${SCOPE_ASSETS_SEGMENT}/${encodeURIComponent(assetId)}/${encodeURIComponent(screenIdToUrlSlug(screenId))}`
  }

  if (scope === 'in-an-emergency') {
    if (screenId === 'screen.inAnEmergency') {
      return `/operators/${opSeg}/${SCOPE_EMERGENCY_SEGMENT}`
    }
    if (!assetId) return `/operators/${opSeg}/${SCOPE_EMERGENCY_SEGMENT}`
    if (screenId === 'screen.assetEmergencyResponse') {
      return `/operators/${opSeg}/${SCOPE_EMERGENCY_SEGMENT}/${encodeURIComponent(assetId)}`
    }
    return `/operators/${opSeg}/${SCOPE_EMERGENCY_SEGMENT}/${encodeURIComponent(assetId)}/${encodeURIComponent(screenIdToUrlSlug(screenId))}`
  }

  return `/operators/${opSeg}`
}

export function pathForMenuStackPrefix(
  operatorId: string,
  stackPrefix: string[],
  assetId: string | null,
  urlSlugForOperator?: OperatorUrlSlugResolver,
): string {
  const route = routeFromMenuStack(stackPrefix, assetId)
  return pathForOperatorRoute(operatorId, route, urlSlugForOperator)
}
